import { useRevalidator } from "@remix-run/react";
import { useRef, useEffect } from "react";

export default function useReturnFromExternal(onReturn?: () => void) {
  const { revalidate } = useRevalidator();
  const hasExecutedRef = useRef(false);
  const navigationStartTime = useRef<number | null>(null);

  useEffect(() => {
    // Track when user starts navigating away
    function onBeforeUnload() {
      navigationStartTime.current = Date.now();
    }

    // Only fired when coming back from BFCache (actual back/forward navigation)
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted && !hasExecutedRef.current) {
        hasExecutedRef.current = true;
        revalidate();
        onReturn?.();
      }
    }

    // More restrictive visibility change - only for actual navigation returns
    function onVisibility() {
      if (document.visibilityState === "visible" && !hasExecutedRef.current) {
        // Only trigger if we previously tracked a navigation attempt
        // AND enough time has passed to indicate external navigation
        if (
          navigationStartTime.current &&
          Date.now() - navigationStartTime.current > 1000
        ) {
          hasExecutedRef.current = true;
          revalidate();
          onReturn?.();
        }
      }
    }

    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [onReturn, revalidate]);

  // If the page actually *reloaded* due to back/forward
  useEffect(() => {
    if (hasExecutedRef.current) return;

    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav?.type === "back_forward") {
      hasExecutedRef.current = true;
      revalidate();
      onReturn?.();
    }
  }, [onReturn, revalidate]);
}

// Alternative: Even more restrictive version that only uses pageshow
export function useReturnFromExternalStrict(onReturn?: () => void) {
  const { revalidate } = useRevalidator();
  const hasExecutedRef = useRef(false);

  useEffect(() => {
    // Only fired when coming back from BFCache (actual back/forward navigation)
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted && !hasExecutedRef.current) {
        hasExecutedRef.current = true;
        revalidate();
        onReturn?.();
      }
    }

    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [onReturn, revalidate]);

  // Back/forward navigation detection
  useEffect(() => {
    if (hasExecutedRef.current) return;

    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (nav?.type === "back_forward") {
      hasExecutedRef.current = true;
      revalidate();
      onReturn?.();
    }
  }, [onReturn, revalidate]);
}
